export const CONTENT_DRAFT_OUTPUT_TYPES = [
  "Article",
  "Comparison page",
  "Landing page section",
  "FAQ block",
  "Title/meta update",
  "Local service page",
  "Product/category page",
  "Entity foundation update",
  "Manual review",
];

export const CONTENT_DRAFT_STATUSES = [
  "Not ready",
  "Requires action brief",
  "Ready for draft",
  "Draft prepared",
  "Coming soon",
];

function text(value, fallback = "") {
  const normalized = String(value ?? "").replace(/\s+/g, " ").trim();
  return normalized || fallback;
}

function normalizeOutputType(value, source) {
  const requested = text(value);
  if (CONTENT_DRAFT_OUTPUT_TYPES.includes(requested)) return requested;
  const key = `${source.activationType || ""} ${source.recommendationType || ""} ${source.entityArea || ""} ${source.actionType || ""} ${source.title || ""}`.toLowerCase();
  if (key.includes("comparison")) return "Comparison page";
  if (key.includes("landing")) return "Landing page section";
  if (key.includes("faq")) return "FAQ block";
  if (key.includes("title") || key.includes("meta")) return "Title/meta update";
  if (key.includes("local") || key.includes("service") || key.includes("location")) return "Local service page";
  if (key.includes("product") || key.includes("category")) return "Product/category page";
  if (key.includes("entity") || key.includes("schema") || key.includes("alias")) return "Entity foundation update";
  if (key.includes("article") || key.includes("content") || key.includes("proof")) return "Article";
  return "Manual review";
}

function normalizeActionBriefStatus(value) {
  const normalized = text(value).toLowerCase();
  return normalized === "ready" || normalized === "completed" || normalized === "draft_prepared" || normalized === "draft prepared"
    ? "ready"
    : "missing";
}

export function buildContentDraftOutputModel(input = {}) {
  const safeInput = input && typeof input === "object" ? input : {};
  const source = safeInput.recommendation && typeof safeInput.recommendation === "object"
    ? { ...safeInput.recommendation, ...safeInput }
    : safeInput;
  const recommendationId = text(source.recommendationId || source.recommendation_id || source.id || source.key);
  const accountId = text(source.accountId || source.account_id);
  const runId = text(source.runId || source.run_id);
  const actionBriefId = text(source.actionBriefId || source.action_brief_id);
  const actionBriefReady = actionBriefId || source.actionBriefReady === true || normalizeActionBriefStatus(source.actionBriefStatus || source.action_brief_status) === "ready";
  const outputType = normalizeOutputType(source.outputType || source.output_type, source);
  const targetAsset = text(source.targetAsset || source.target_asset || source.title, "Target asset unavailable");
  const objective = text(source.objective || source.goal || source.description || source.summary, "Recommended output pending reviewed signal.");
  const evidenceReason = text(source.evidenceReason || source.evidence_reason || source.whyItMatters || source.why_it_matters || source.description, "Review evidence before creating the next asset.");
  const primaryNextAction = text(source.primaryNextAction || source.primary_next_action || source.recommendedAction || source.recommended_action || source.action, "Review evidence before creating the next asset.");
  const audience = text(source.audience || source.targetAudience || source.target_audience || source.forAudience || source.for_audience || source.entityName || source.entity_name || source.entityArea || source.entity_area, "The audience most likely to rely on this decision.");
  const evidencePattern = text(source.evidencePattern || source.evidence_pattern || source.evidenceSignal || source.evidence_signal || source.sourceInsight || source.source_insight || source.evidence || source.whyItMatters || source.why_it_matters || source.description, "A reviewed visibility or recommendation gap is driving this prescription.");
  const expectedResult = text(source.expectedResult || source.expected_result || source.deliverableOutcome || source.deliverable_outcome || source.desiredOutcome || source.desired_outcome || source.objective || source.goal || source.description || source.summary, "A clearer reviewed asset ready for the next human review.");
  const platformFit = text(source.platformFit || source.platform_fit, "Platform fit not specified.");
  const sourceRecommendationTitle = text(source.sourceRecommendationTitle || source.source_recommendation_title || source.title, "No source recommendation available");
  const draftStatus = source.draftPrepared === true
    ? "Draft prepared"
    : actionBriefReady
      ? "Ready for draft"
      : recommendationId
        ? "Requires action brief"
        : "Not ready";
  const idempotencyKey = [
    "content-draft",
    accountId || "account-unavailable",
    runId || "run-unavailable",
    recommendationId || "recommendation-unavailable",
    actionBriefId || "action-brief-required",
    outputType,
  ].map((part) => text(part).toLowerCase().replace(/[^a-z0-9._-]+/g, "-")).join(":");

  return {
    recommendationId: recommendationId || null,
    accountId: accountId || null,
    runId: runId || null,
    actionBriefId: actionBriefId || null,
    actionBriefRequired: !Boolean(actionBriefReady),
    outputType,
    targetAsset,
    objective,
    evidenceReason,
    primaryNextAction,
    audience,
    evidencePattern,
    expectedResult,
    draftStatus,
    platformFit,
    sourceRecommendationTitle,
    idempotencyKey,
  };
}
